import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/swiper-bundle.css';

/**
 * SwiperImageCarousel class initializes Swiper.js carousels with analytics tracking.
 */
class SwiperImageCarousel {
  /**
   * Create a new SwiperImageCarousel instance.
   * @param {HTMLElement[]} elements - An array of DOM elements to initialize Swiper on.
   */
  constructor(elements) {
    this.elements = elements;
    this.init();
  }

  /**
   * Initializes Swiper carousels for each element in `this.elements`.
   */
  init() {
    this.elements.forEach(swiperElement => {
      if (!swiperElement) return;

      new Swiper(swiperElement, {
        autoHeight: false,
        slidesPerView: 1,
        loop: true,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        modules: [Navigation, Pagination],
        watchSlidesProgress: true,

        // Allow pinch-to-zoom and scroll gestures
        passiveListeners: true,

        // Lazy load nearby images for performance
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 1,
        },

        // Use fraction-style pagination (e.g., 1/5)
        pagination: {
          el: '.swiper-pagination',
          type: 'fraction',
        },

        // Track slide changes for analytics
        on: {
          slideChange: swiper => SwiperImageCarousel.sendAnalytics(swiper, swiperElement),
        },
      });
    });
  }

  /**
   * Tracks the time of the last analytics event to prevent spamming.
   * @type {number}
   * @private
   */
  static _lastEventTime = 0;

  /**
   * Stores last reported slide index for each swiper element.
   * @type {Map<string, number>}
   * @private
   */
  static _lastIndexes = new Map();

  /**
   * Sends a custom analytics event to dataLayer on slide change.
   * Avoids duplicate events and fires only on real user interactions.
   * Uses vehicle_id from the closest parent link as the unique key.
   * Throttles events to fire at most once per second.
   *
   * @param {Swiper} swiper - Swiper instance that triggered the event.
   * @param {HTMLElement} swiperElement - DOM element the swiper is initialized on.
   */
  static sendAnalytics(swiper, swiperElement) {
    const now = Date.now();
    const interval = 1000;

    // Find the closest parent link and get its dataset
    const closestLink = swiperElement.closest('a');
    const dataSet = closestLink?.dataset || {};
    const vehicleId = dataSet.vehicle_id;
    if (!vehicleId) return; // No ID, no analytics

    // Ensure index is a valid number
    if (
      typeof swiper.activeIndex !== 'number' ||
      typeof swiper.realIndex !== 'number' ||
      swiper.activeIndex === null ||
      swiper.realIndex === null ||
      Number.isNaN(swiper.activeIndex) ||
      Number.isNaN(swiper.realIndex)
    ) {
      return;
    }

    const last = this._lastIndexes.get(vehicleId);

    // On first call or if Swiper instance changed, just store and do not fire analytics
    if (typeof last === 'undefined' || last.swiper !== swiper) {
      this._lastIndexes.set(vehicleId, { index: swiper.realIndex, swiper });
      return;
    }
    // Only fire if the index has changed
    if (last.index === swiper.realIndex) return;

    this._lastIndexes.set(vehicleId, { index: swiper.realIndex, swiper });

    // Throttle: fire at most once per interval
    if (!this._lastEventTime || now - this._lastEventTime > interval) {
      // Final check before sending
      if (typeof swiper.activeIndex !== 'number' || swiper.activeIndex === null || Number.isNaN(swiper.activeIndex)) {
        return;
      }
      this._lastEventTime = now;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'vehicle_search_carousel',
        carouselIndex: swiper.activeIndex,
        ...dataSet,
      });
    }
  }
}

export { SwiperImageCarousel };
